import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Drawer,
  TextField,
  Snackbar,
  Alert,
  Chip,
  useTheme,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Collapse,
  Switch,
  Tooltip,
  Avatar,
  ListItemAvatar,
  ListItemText,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import DatePicker from '../../shared/components/forms/HotelDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import BuildIcon from '@mui/icons-material/Build';
import MeetingRoomRoundedIcon from '@mui/icons-material/MeetingRoomRounded';
import KeyboardDoubleArrowUpRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowUpRounded';
import KeyboardDoubleArrowDownRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowDownRounded';
import DragHandleRoundedIcon from '@mui/icons-material/DragHandleRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import BlockIcon from '@mui/icons-material/DoDisturbOnRounded';
import moment from 'moment';
import {
  getMaintenanceRequests,
  createMaintenanceRequest,
  updateMaintenanceRequest,
  deleteMaintenanceRequest,
} from '../../services/api';
import { getConfigurationRooms, getHousekeepingStaff } from '../../services/api';
import { fetchBlockRoomAvailability } from '../../services/inventoryBlockService';
import { detectThemeSync } from '../../utils/themeUtils';
import {
  getHousekeepingDrawerFooterSx,
  getHousekeepingDrawerHeaderSx,
  getHousekeepingDrawerPaperSx,
  getHousekeepingListPaperSx,
  getHousekeepingThemeColors,
} from '../../utils/housekeepingMobileLayout';
import {
  createDrawerOverlayProps,
  getDrawerRootSx,
} from '../../utils/drawerOverlayProps';
import { getSoftFieldSx, SectionLabel, SelectableChip } from './housekeepingFormKit';

const THEME_COLOR = '#4ce1b6';

const PRIORITY_CHOICES = [
  { value: 'LOW', label: 'Low', color: '#4caf50', icon: <KeyboardDoubleArrowDownRoundedIcon /> },
  { value: 'MEDIUM', label: 'Medium', color: '#ff9800', icon: <DragHandleRoundedIcon /> },
  { value: 'HIGH', label: 'High', color: '#f44336', icon: <KeyboardDoubleArrowUpRoundedIcon /> },
];

const STATUS_CHOICES = [
  { value: 'OPEN', label: 'Open', color: '#42a5f5', icon: <ErrorOutlineRoundedIcon /> },
  { value: 'FIXED', label: 'Fixed', color: '#4caf50', icon: <CheckCircleRoundedIcon /> },
];

// Rotating accent palette for issue category chips
const CATEGORY_CHIP_COLORS = [
  '#4ce1b6', '#42a5f5', '#ab47bc', '#ff7043', '#26c6da', '#ffca28',
  '#ec407a', '#66bb6a', '#7e57c2', '#29b6f6', '#ffa726', '#8d6e63',
];

// Issue type categories and sub-options (category dropdown → sub-option dropdown)
const MAINTENANCE_ISSUE_OPTIONS = [
  {
    category: 'Room Condition Issues',
    options: [
      'Dirty room / poor housekeeping',
      'Bad odor / smell',
      'Stained bedsheets / linens',
      'Dusty furniture',
      'Pest issues (cockroaches, insects, bed bugs)',
      'Damaged walls / paint peeling',
      'Broken furniture (chair, table, bed)',
    ],
  },
  {
    category: 'Bathroom Issues',
    options: [
      'No water / low water pressure',
      'No hot water',
      'Leaking taps / pipes',
      'Blocked drain / toilet',
      'Dirty bathroom',
      'Broken shower / fittings',
      'No toiletries (soap, shampoo, towels)',
    ],
  },
  {
    category: 'Electrical & Appliance Issues',
    options: [
      'AC not working / poor cooling',
      'Fan not working',
      'TV not working',
      'Remote not working',
      'Power outage / no electricity',
      'Faulty switches / plug points',
      'Lights not working',
    ],
  },
  {
    category: 'Internet & Connectivity',
    options: [
      'WiFi not working',
      'Slow internet speed',
      'Frequent disconnection',
    ],
  },
  {
    category: 'Noise & Disturbance',
    options: [
      'Noise from outside traffic',
      'Noise from other guests',
      'Construction noise',
      'Staff disturbance',
    ],
  },
  {
    category: 'Safety & Security Issues',
    options: [
      'Door lock not working',
      'Broken latch / chain lock',
      'Unsafe balcony',
      'Missing safety features (fire alarm, extinguisher)',
    ],
  },
  {
    category: 'Comfort Issues',
    options: [
      'Room too small',
      'Poor ventilation',
      'Bad room temperature',
      'Uncomfortable bed / mattress',
      'Insufficient blankets',
    ],
  },
  {
    category: 'Food & Room Service Issues',
    options: [
      'Late room service',
      'Wrong order delivered',
      'Poor food quality',
      'Limited menu options',
    ],
  },
  {
    category: 'Booking & Pricing Issues',
    options: [
      'Room not as shown in photos',
      'Overbooking / no room available',
      'Wrong room category given',
      'Extra charges applied',
      'Early check-in denied',
    ],
  },
  {
    category: 'Housekeeping & Service Issues',
    options: [
      'Room not cleaned on time',
      'Staff behavior issues',
      'Slow service response',
      'No daily housekeeping',
    ],
  },
  {
    category: 'Amenities Issues',
    options: [
      'Missing items (kettle, bottle, glass, etc.)',
      'Mini fridge not working',
      'No drinking water provided',
      'No wardrobe / hangers',
    ],
  },
  {
    category: 'Technology Issues (Smart Hotels)',
    options: [
      'Key card not working',
      'App check-in failure',
      'Smart controls not working',
    ],
  },
];

const EMPTY_MAINTENANCE_FORM = {
  room: '',
  reported_by: '',
  issue_category: '',
  issue_type: '',
  priority: 'MEDIUM',
  status: 'OPEN',
  description: '',
  fixed_date: null,
  fixed_by: '',
  notes: '',
  block_room: false,
  block_start_date: null,
  block_end_date: null,
};

const ROOM_NOT_EMPTY_MESSAGE =
  'Only empty rooms can be blocked for the selected dates. This room is already allocated or checked in — choose a free room, or change Blocked From / Blocked Until.';

/** Normalize API / thrown error payloads into a single string. */
const extractApiErrorMessage = (error, field = 'block_room') => {
  if (!error) return null;
  const payload = error?.response?.data || error;
  const fieldError = payload?.errors?.[field] ?? payload?.[field];
  if (Array.isArray(fieldError)) return fieldError.filter(Boolean).join(' ');
  if (typeof fieldError === 'string' && fieldError.trim()) return fieldError;
  if (typeof payload?.error === 'string') return payload.error;
  if (typeof payload?.detail === 'string') return payload.detail;
  if (typeof error?.userMessage === 'string') return error.userMessage;
  if (typeof error?.message === 'string' && !error.message.startsWith('Request failed')) {
    return error.message;
  }
  return null;
};

const MaintenanceRequestSection = ({ onRefreshRoomStatus }) => {
  const theme = useTheme();
  const isDark = detectThemeSync() === 'dark';
  const themeColors = getHousekeepingThemeColors(isDark);
  const drawerOverlays = useMemo(
    () => createDrawerOverlayProps(isDark, isDark ? themeColors.surface : '#ffffff'),
    [isDark, themeColors.surface]
  );
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [requestList, setRequestList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [editingRequest, setEditingRequest] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [rooms, setRooms] = useState([]);
  const [staffList, setStaffList] = useState([]);

  const [formData, setFormData] = useState({ ...EMPTY_MAINTENANCE_FORM });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Sub-options for the selected issue category
  const issueSubOptions = useMemo(() => {
    if (!formData.issue_category) return [];
    const found = MAINTENANCE_ISSUE_OPTIONS.find((c) => c.category === formData.issue_category);
    return found ? found.options : [];
  }, [formData.issue_category]);

  useEffect(() => {
    fetchRequests();
    fetchRooms();
    fetchStaff();
  }, []);

  /**
   * Confirm the selected room is empty for Blocked From → Blocked Until.
   * Returns an error message, or null when the room can be blocked.
   */
  const validateRoomEmptyForBlockRange = useCallback(async () => {
    if (!formData.block_room || !formData.room || !formData.block_start_date || !formData.block_end_date) {
      return null;
    }
    if (formData.block_end_date.isBefore(formData.block_start_date, 'day')) {
      return null;
    }

    const startDate = formData.block_start_date.format('YYYY-MM-DD');
    const endDate = formData.block_end_date.format('YYYY-MM-DD');
    const roomId = Number(formData.room);
    const selectedRoom = rooms.find((r) => Number(r.id) === roomId);
    const roomLabel = selectedRoom?.room_number || selectedRoom?.name || String(formData.room);

    try {
      const availability = await fetchBlockRoomAvailability(startDate, endDate, {
        excludeBlockId: editingRequest?.inventory_block_id || undefined,
      });
      const blockedIds = new Set((availability.room_ids || []).map(Number));
      if (blockedIds.has(roomId)) {
        return (
          `Room ${roomLabel} is already allocated or checked in between ${moment(startDate).format('DD MMM YYYY')} and ${moment(endDate).format('DD MMM YYYY')}. ` +
          'Only empty rooms can be blocked — choose a free room, or change Blocked From / Blocked Until.'
        );
      }

      const typeKey = String(selectedRoom?.room_type_id || selectedRoom?.room_type?.id || '');
      const capacity = typeKey ? availability.capacity_by_room_type?.[typeKey] : null;
      if (capacity && Number(capacity.max_blockable) <= 0) {
        return (
          `No empty ${selectedRoom?.room_type_name || 'rooms'} left to block for these dates ` +
          `(${capacity.booked || 0} already booked). Change the date range or pick another room.`
        );
      }
    } catch (err) {
      // Fall through to server-side validation on save if the availability check fails.
      console.warn('Room block availability check failed', err);
    }
    return null;
  }, [
    formData.block_room,
    formData.room,
    formData.block_start_date,
    formData.block_end_date,
    editingRequest?.inventory_block_id,
    rooms,
  ]);

  // Live check: room must be empty for Blocked From → Blocked Until.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (
        !formData.block_room ||
        !formData.room ||
        !formData.block_start_date ||
        !formData.block_end_date ||
        formData.block_end_date.isBefore(formData.block_start_date, 'day')
      ) {
        setErrors((prev) => (prev.block_room ? { ...prev, block_room: null } : prev));
        return;
      }
      const message = await validateRoomEmptyForBlockRange();
      if (cancelled) return;
      setErrors((prev) => ({ ...prev, block_room: message || null }));
    };
    const timer = setTimeout(run, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    formData.block_room,
    formData.room,
    formData.block_start_date,
    formData.block_end_date,
    validateRoomEmptyForBlockRange,
  ]);

  const fetchRooms = async () => {
    try {
      const roomsData = await getConfigurationRooms();
      const allRooms = [];
      roomsData.forEach(floor => {
        if (floor.rooms && floor.rooms.length > 0) {
          floor.rooms.forEach(room => {
            allRooms.push({
              id: room.id,
              name: `${room.room_number} (${room.room_type_name})`,
              room_number: room.room_number,
              room_type_id: room.room_type_id || room.room_type?.id || null,
              room_type_name: room.room_type_name || '',
              image_url: room.room_type_image_url || room.image_url || room.image || null,
            });
          });
        }
      });
      setRooms(allRooms);
    } catch (error) {}
  };

  const fetchStaff = async () => {
    try {
      const response = await getHousekeepingStaff({ page_size: 1000 });
      setStaffList(response.results || []);
    } catch (error) {}
  };

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) {
        params.search = search;
      }
      params.page = page + 1;
      params.page_size = rowsPerPage;
      const response = await getMaintenanceRequests(params);
      setRequestList(response.results || []);
    } catch (error) {setSnackbar({
        open: true,
        message: error.userMessage || 'Failed to load maintenance requests',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [page, rowsPerPage, search]);

  const handleOpenDrawer = (request = null) => {
    if (request) {
      setEditingRequest(request);
      const roomId = typeof request.room === 'object' && request.room != null ? request.room.id : (request.room_id ?? request.room ?? '');
      const reportedById = typeof request.reported_by === 'object' && request.reported_by != null ? request.reported_by.id : (request.reported_by_id ?? request.reported_by ?? '');
      const issueType = request.issue_type || '';
      const categoryForIssue = issueType
        ? MAINTENANCE_ISSUE_OPTIONS.find((c) => c.options.includes(issueType))?.category || ''
        : '';
      setFormData({
        room: roomId === '' || roomId == null ? '' : (Number(roomId) || roomId),
        reported_by: reportedById === '' || reportedById == null ? '' : (Number(reportedById) || reportedById),
        issue_category: categoryForIssue,
        issue_type: issueType,
        priority: request.priority || 'MEDIUM',
        status: request.status || 'OPEN',
        description: request.description || '',
        fixed_date: request.fixed_date ? moment(request.fixed_date) : null,
        fixed_by: request.fixed_by || '',
        notes: request.notes || '',
        block_room: !!request.block_room,
        block_start_date: request.block_start_date ? moment(request.block_start_date) : null,
        block_end_date: request.block_end_date ? moment(request.block_end_date) : null,
      });
    } else {
      setEditingRequest(null);
      setFormData({ ...EMPTY_MAINTENANCE_FORM });
    }
    setErrors({});
    setOpenDrawer(true);
  };

  const handleCloseDrawer = () => {
    if (saving) return;
    setOpenDrawer(false);
    setEditingRequest(null);
    setFormData({ ...EMPTY_MAINTENANCE_FORM });
    setErrors({});
    setSaving(false);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'issue_category') {
        next.issue_type = '';
      }
      return next;
    });
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.room) {
      newErrors.room = 'Room is required';
    }
    if (!formData.issue_category) {
      newErrors.issue_category = 'Please select an issue category';
    }
    if (!formData.issue_type || !String(formData.issue_type).trim()) {
      newErrors.issue_type = 'Please select an issue';
    }
    if (formData.block_room) {
      if (!formData.block_start_date) {
        newErrors.block_start_date = 'Select the first date the room is out of sale';
      }
      if (!formData.block_end_date) {
        newErrors.block_end_date = 'Select the last date the room is out of sale';
      }
      if (
        formData.block_start_date &&
        formData.block_end_date &&
        formData.block_end_date.isBefore(formData.block_start_date, 'day')
      ) {
        newErrors.block_end_date = 'End date must be on or after the start date';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (saving) return;
    if (!validateForm()) return;

    if (formData.block_room) {
      const emptyRoomError = await validateRoomEmptyForBlockRange();
      if (emptyRoomError) {
        setErrors((prev) => ({ ...prev, block_room: emptyRoomError }));
        setSnackbar({
          open: true,
          message: emptyRoomError,
          severity: 'error',
        });
        return;
      }
    }

    setSaving(true);
    try {
      const dataToSend = {
        room: formData.room,
        reported_by: formData.reported_by || null,
        issue_type: formData.issue_type,
        priority: formData.priority,
        status: formData.status,
        description: formData.description,
        fixed_date: formData.fixed_date ? formData.fixed_date.format('YYYY-MM-DD') : null,
        fixed_by: formData.fixed_by || '',
        notes: formData.notes,
        block_room: !!formData.block_room,
        block_start_date: formData.block_room && formData.block_start_date
          ? formData.block_start_date.format('YYYY-MM-DD')
          : null,
        block_end_date: formData.block_room && formData.block_end_date
          ? formData.block_end_date.format('YYYY-MM-DD')
          : null,
      };

      if (editingRequest) {
        await updateMaintenanceRequest(editingRequest.id, dataToSend);
        setSnackbar({
          open: true,
          message: 'Maintenance request updated successfully',
          severity: 'success',
        });
      } else {
        await createMaintenanceRequest(dataToSend);
        setSnackbar({
          open: true,
          message: 'Maintenance request created successfully',
          severity: 'success',
        });
      }
      handleCloseDrawer();
      fetchRequests();
      onRefreshRoomStatus?.();
    } catch (error) {
      const duplicateError = extractApiErrorMessage(error, 'issue_type')
        || extractApiErrorMessage(error, 'non_field_errors');
      const blockError = extractApiErrorMessage(error, 'block_room') || (
        formData.block_room && !duplicateError ? ROOM_NOT_EMPTY_MESSAGE : null
      );
      if (blockError && formData.block_room) {
        setErrors((prev) => ({ ...prev, block_room: blockError }));
      }
      if (duplicateError) {
        setErrors((prev) => ({ ...prev, issue_type: duplicateError }));
      }
      setSnackbar({
        open: true,
        message:
          duplicateError
          || blockError
          || extractApiErrorMessage(error)
          || 'Failed to save maintenance request',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (request) => {
    setRequestToDelete(request);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteMaintenanceRequest(requestToDelete.id);
      setSnackbar({
        open: true,
        message: 'Maintenance request deleted successfully',
        severity: 'success',
      });
      setDeleteDialogOpen(false);
      setRequestToDelete(null);
      fetchRequests();
      onRefreshRoomStatus?.();
    } catch (error) {setSnackbar({
        open: true,
        message: error.userMessage || 'Failed to delete maintenance request',
        severity: 'error',
      });
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredRequests = useMemo(() => {
    if (!search) return requestList;
    const searchLower = search.toLowerCase();
    return requestList.filter(
      (request) =>
        (request.room_number || request.room?.room_number || '')?.toLowerCase().includes(searchLower) ||
        request.issue_type?.toLowerCase().includes(searchLower)
    );
  }, [requestList, search]);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'HIGH':
        return '#f44336';
      case 'MEDIUM':
        return '#ff9800';
      case 'LOW':
        return '#4caf50';
      default:
        return '#999';
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box>
        <Paper
          elevation={0}
        sx={{
          ...getHousekeepingListPaperSx(isDark),
          p: { xs: 1.5, sm: 2, md: 2.5 },
        }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ color: isDark ? '#ddd' : '#333', fontWeight: 600 }}>
              Maintenance Requests
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDrawer()}
              sx={{
                backgroundColor: THEME_COLOR,
                color: '#000',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: THEME_COLOR,
                  opacity: 0.9,
                },
              }}
            >
              Add Request
            </Button>
          </Box>

          <TextField
            fullWidth
            placeholder="Search by room number or issue type..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: <BuildIcon sx={{ mr: 1, color: isDark ? '#999' : '#666' }} />,
            }}
          />

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Room</TableCell>
                      <TableCell>Issue Type</TableCell>
                      <TableCell>Priority</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Reported By</TableCell>
                      <TableCell>Reported Date</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRequests.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography variant="body2" sx={{ color: isDark ? '#999' : '#666', py: 2 }}>
                            No maintenance requests found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredRequests.map((request) => {
                        const roomId = typeof request.room === 'object' && request.room != null
                          ? request.room.id
                          : (request.room_id ?? request.room);
                        const roomMeta = rooms.find(
                          (r) => r.id === roomId || String(r.id) === String(roomId)
                            || (request.room_number && r.room_number === request.room_number)
                        );
                        const roomLabel = request.room_number || request.room?.room_number || 'N/A';
                        const roomTypeLabel = request.room_type_name || roomMeta?.room_type_name || '';
                        return (
                        <TableRow key={request.id} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                              <Avatar
                                src={roomMeta?.image_url || undefined}
                                variant="rounded"
                                sx={{
                                  width: 40,
                                  height: 40,
                                  bgcolor: isDark ? '#1f2a28' : '#e6f9f2',
                                  color: THEME_COLOR,
                                  flexShrink: 0,
                                }}
                              >
                                <MeetingRoomRoundedIcon fontSize="small" />
                              </Avatar>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                                  {roomLabel}
                                </Typography>
                                {roomTypeLabel ? (
                                  <Typography
                                    variant="caption"
                                    sx={{ color: isDark ? '#8a939e' : '#78828c', display: 'block', lineHeight: 1.2 }}
                                  >
                                    {roomTypeLabel}
                                  </Typography>
                                ) : null}
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>{request.issue_type || 'N/A'}</TableCell>
                          <TableCell>
                            <Chip
                              label={PRIORITY_CHOICES.find((p) => p.value === request.priority)?.label || request.priority}
                              size="small"
                              sx={{
                                backgroundColor: getPriorityColor(request.priority),
                                color: '#fff',
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <Chip
                                label={STATUS_CHOICES.find((s) => s.value === request.status)?.label || request.status}
                                size="small"
                                sx={{
                                  backgroundColor: request.status === 'FIXED' ? '#4caf50' : '#999',
                                  color: '#fff',
                                }}
                              />
                              {request.block_room && request.status === 'OPEN' && (
                                <Tooltip
                                  title={
                                    request.block_start_date && request.block_end_date
                                      ? `Room out of sale ${moment(request.block_start_date).format('DD MMM')} – ${moment(request.block_end_date).format('DD MMM')}. Inventory reduced on PMS and OTAs.`
                                      : 'Room out of sale. Inventory reduced on PMS and OTAs.'
                                  }
                                >
                                  <Chip
                                    icon={<BlockIcon sx={{ fontSize: 14, color: '#fff !important' }} />}
                                    label="Blocked"
                                    size="small"
                                    sx={{ backgroundColor: '#ef5350', color: '#fff', fontWeight: 600 }}
                                  />
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>{request.reported_by_name || request.reported_by?.full_name || 'N/A'}</TableCell>
                          <TableCell>{request.reported_date ? moment(request.reported_date).format('DD/MM/YYYY') : 'N/A'}</TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDrawer(request)}
                              sx={{ color: THEME_COLOR }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(request)}
                              sx={{ color: '#f44336' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={filteredRequests.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          )}
        </Paper>

        {/* Add/Edit Drawer */}
        <Drawer
          anchor="right"
          open={openDrawer}
          onClose={handleCloseDrawer}
          sx={getDrawerRootSx()}
          PaperProps={{
            sx: {
              width: { xs: '100%', sm: 600 },
              ...getHousekeepingDrawerPaperSx(isDark),
              backgroundColor: isDark ? themeColors.surface : '#ffffff',
              backgroundImage: 'none',
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
            },
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              ...getHousekeepingDrawerHeaderSx(isDark),
              backgroundColor: isDark ? themeColors.headerBg : '#ffffff',
              backgroundImage: 'none',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: isDark ? '#1f2a28' : '#e6f9f2',
                }}
              >
                <BuildIcon sx={{ color: THEME_COLOR, fontSize: 22 }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ color: isDark ? '#eee' : '#222', fontWeight: 700, lineHeight: 1.2 }}>
                  {editingRequest ? 'Edit Maintenance Request' : 'Add Maintenance Request'}
                </Typography>
                <Typography variant="caption" sx={{ color: isDark ? '#8a939e' : '#78828c' }}>
                  {editingRequest ? 'Update the details below' : 'Log a new issue for a room'}
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={handleCloseDrawer}
              disabled={saving}
              sx={{
                color: isDark ? '#999' : '#666',
                backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                '&:hover': { backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)' },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <FormControl fullWidth required error={!!errors.room} sx={getSoftFieldSx(isDark)}>
              <InputLabel>Room</InputLabel>
              <Select
                value={formData.room}
                onChange={(e) => handleInputChange('room', e.target.value)}
                label="Room"
                renderValue={(selected) => {
                  const room = rooms.find((r) => r.id === selected || String(r.id) === String(selected));
                  if (!room) return '';
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <Avatar
                        src={room.image_url || undefined}
                        variant="rounded"
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: isDark ? '#1f2a28' : '#e6f9f2',
                          color: THEME_COLOR,
                          fontSize: 16,
                        }}
                      >
                        <MeetingRoomRoundedIcon fontSize="small" />
                      </Avatar>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {room.name}
                      </Typography>
                    </Box>
                  );
                }}
                MenuProps={{
                  ...drawerOverlays.selectMenuProps,
                  PaperProps: {
                    ...drawerOverlays.selectMenuProps.PaperProps,
                    sx: {
                      ...drawerOverlays.selectMenuProps.PaperProps?.sx,
                      maxHeight: 360,
                    },
                  },
                }}
              >
                {rooms.map((room) => (
                  <MenuItem
                    key={room.id}
                    value={room.id}
                    sx={{
                      py: 1,
                      gap: 0.5,
                      '&.Mui-selected': {
                        backgroundColor: isDark ? 'rgba(76, 225, 182, 0.12)' : 'rgba(76, 225, 182, 0.1)',
                      },
                      '&.Mui-selected:hover': {
                        backgroundColor: isDark ? 'rgba(76, 225, 182, 0.18)' : 'rgba(76, 225, 182, 0.16)',
                      },
                    }}
                  >
                    <ListItemAvatar sx={{ minWidth: 48 }}>
                      <Avatar
                        src={room.image_url || undefined}
                        variant="rounded"
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: isDark ? '#1f2a28' : '#e6f9f2',
                          color: THEME_COLOR,
                        }}
                      >
                        <MeetingRoomRoundedIcon fontSize="small" />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={room.room_number}
                      secondary={room.room_type_name}
                      primaryTypographyProps={{ fontWeight: 700, fontSize: '0.95rem' }}
                      secondaryTypographyProps={{
                        fontSize: '0.75rem',
                        color: isDark ? '#8a939e' : '#78828c',
                      }}
                    />
                  </MenuItem>
                ))}
              </Select>
              {errors.room && <FormHelperText>{errors.room}</FormHelperText>}
            </FormControl>

            <FormControl fullWidth sx={getSoftFieldSx(isDark)}>
              <InputLabel>Reported By</InputLabel>
              <Select
                value={formData.reported_by}
                onChange={(e) => handleInputChange('reported_by', e.target.value)}
                label="Reported By"
                MenuProps={drawerOverlays.selectMenuProps}
              >
                <MenuItem value="">Not specified</MenuItem>
                {staffList.map((staff) => (
                  <MenuItem key={staff.id} value={staff.id}>
                    {staff.full_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Issue Type (Category) — chip cloud */}
            <Box>
              <SectionLabel required isDark={isDark}>Issue Type</SectionLabel>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {MAINTENANCE_ISSUE_OPTIONS.map((item, idx) => (
                  <SelectableChip
                    key={item.category}
                    label={item.category}
                    size="small"
                    selected={formData.issue_category === item.category}
                    color={CATEGORY_CHIP_COLORS[idx % CATEGORY_CHIP_COLORS.length]}
                    isDark={isDark}
                    onClick={() => handleInputChange('issue_category', item.category)}
                  />
                ))}
              </Box>
              {errors.issue_category && (
                <FormHelperText error sx={{ mx: 0, mt: 0.75 }}>{errors.issue_category}</FormHelperText>
              )}
            </Box>

            {/* Issue (Sub-option) — shown once a category is picked.
                Conditional render (not Collapse) so wrapped chips always reserve height
                and Priority/Status never stack underneath. */}
            {formData.issue_category ? (
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: '14px',
                  backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                }}
              >
                <SectionLabel required isDark={isDark}>Issue — {formData.issue_category}</SectionLabel>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {issueSubOptions.map((opt) => (
                    <SelectableChip
                      key={opt}
                      label={opt}
                      size="small"
                      selected={formData.issue_type === opt}
                      color={THEME_COLOR}
                      isDark={isDark}
                      onClick={() => handleInputChange('issue_type', opt)}
                    />
                  ))}
                </Box>
                {errors.issue_type && (
                  <FormHelperText error sx={{ mx: 0, mt: 0.75 }}>{errors.issue_type}</FormHelperText>
                )}
              </Box>
            ) : null}

            {/* Priority + Status — stacked full-width so they never collide with chips above */}
            <Box>
              <SectionLabel isDark={isDark}>Priority</SectionLabel>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {PRIORITY_CHOICES.map((priority) => (
                  <SelectableChip
                    key={priority.value}
                    label={priority.label}
                    icon={priority.icon}
                    selected={formData.priority === priority.value}
                    color={priority.color}
                    isDark={isDark}
                    onClick={() => handleInputChange('priority', priority.value)}
                  />
                ))}
              </Box>
            </Box>
            <Box>
              <SectionLabel isDark={isDark}>Status</SectionLabel>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {STATUS_CHOICES.map((status) => (
                  <SelectableChip
                    key={status.value}
                    label={status.label}
                    icon={status.icon}
                    selected={formData.status === status.value}
                    color={status.color}
                    isDark={isDark}
                    onClick={() => handleInputChange('status', status.value)}
                  />
                ))}
              </Box>
            </Box>

            <Box>
              <SectionLabel isDark={isDark}>Room Availability</SectionLabel>
              <Box
                onClick={() => handleInputChange('block_room', !formData.block_room)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.5,
                  borderRadius: '14px',
                  cursor: 'pointer',
                  border: `1.5px solid ${
                    formData.block_room
                      ? '#ef5350'
                      : isDark
                        ? 'rgba(255,255,255,0.08)'
                        : 'rgba(0,0,0,0.06)'
                  }`,
                  backgroundColor: formData.block_room
                    ? isDark
                      ? 'rgba(239, 83, 80, 0.14)'
                      : 'rgba(239, 83, 80, 0.07)'
                    : isDark
                      ? 'rgba(255,255,255,0.03)'
                      : 'rgba(0,0,0,0.02)',
                  transition: 'all 0.2s ease',
                }}
              >
                <BlockIcon
                  sx={{
                    fontSize: 22,
                    color: formData.block_room ? '#ef5350' : isDark ? '#888' : '#9e9e9e',
                  }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: '0.85rem',
                      fontWeight: 700,
                      color: formData.block_room ? '#ef5350' : isDark ? '#ddd' : '#424242',
                    }}
                  >
                    Block this room from sale
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.72rem',
                      color: isDark ? '#8a8a8a' : '#757575',
                      lineHeight: 1.4,
                    }}
                  >
                    Reduces PMS and OTA inventory, and stops the room from being allocated or
                    checked in for these dates.
                  </Typography>
                </Box>
                <Switch
                  checked={!!formData.block_room}
                  onChange={(e) => handleInputChange('block_room', e.target.checked)}
                  onClick={(e) => e.stopPropagation()}
                  size="small"
                  sx={{
                    '& .Mui-checked': { color: '#ef5350' },
                    '& .Mui-checked + .MuiSwitch-track': { backgroundColor: '#ef5350' },
                  }}
                />
              </Box>

              <Collapse in={!!formData.block_room} unmountOnExit>
                <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5 }}>
                  <DatePicker
                    label="Blocked From"
                    value={formData.block_start_date}
                    onChange={(date) => handleInputChange('block_start_date', date)}
                    slotProps={{
                      ...drawerOverlays.datePickerSlotProps,
                      textField: {
                        fullWidth: true,
                        error: !!errors.block_start_date,
                        helperText: errors.block_start_date,
                        sx: getSoftFieldSx(isDark),
                      },
                    }}
                  />
                  <DatePicker
                    label="Blocked Until"
                    value={formData.block_end_date}
                    minDate={formData.block_start_date || undefined}
                    onChange={(date) => handleInputChange('block_end_date', date)}
                    slotProps={{
                      ...drawerOverlays.datePickerSlotProps,
                      textField: {
                        fullWidth: true,
                        error: !!errors.block_end_date,
                        helperText: errors.block_end_date,
                        sx: getSoftFieldSx(isDark),
                      },
                    }}
                  />
                </Box>
              </Collapse>

              {errors.block_room && (
                <Alert
                  severity="error"
                  icon={<BlockIcon fontSize="inherit" />}
                  sx={{
                    mt: 1.25,
                    borderRadius: '12px',
                    alignItems: 'flex-start',
                    '& .MuiAlert-message': { fontSize: '0.8rem', lineHeight: 1.45 },
                  }}
                >
                  {errors.block_room}
                </Alert>
              )}
            </Box>

            <TextField
              label="Description"
              fullWidth
              multiline
              rows={4}
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              sx={getSoftFieldSx(isDark)}
            />

            {formData.status === 'FIXED' && (
              <>
                <DatePicker
                  label="Fixed Date"
                  value={formData.fixed_date}
                  onChange={(date) => handleInputChange('fixed_date', date)}
                  slotProps={{
                    ...drawerOverlays.datePickerSlotProps,
                    textField: {
                      fullWidth: true,
                      sx: getSoftFieldSx(isDark),
                    },
                  }}
                />

                <TextField
                  label="Fixed By"
                  fullWidth
                  value={formData.fixed_by}
                  onChange={(e) => handleInputChange('fixed_by', e.target.value)}
                  sx={getSoftFieldSx(isDark)}
                />
              </>
            )}

            <TextField
              label="Notes"
              fullWidth
              multiline
              rows={3}
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              sx={getSoftFieldSx(isDark)}
            />
          </Box>

          {/* Footer */}
          <Box
            sx={{
              p: 1.5,
              ...getHousekeepingDrawerFooterSx(isDark),
              backgroundColor: isDark ? themeColors.headerBg : '#ffffff',
              backgroundImage: 'none',
              display: 'flex',
              gap: 1.5,
            }}
          >
            <Button
              variant="outlined"
              fullWidth
              onClick={handleCloseDrawer}
              disabled={saving}
              sx={{
                borderRadius: '12px',
                borderColor: themeColors.surfaceBorder,
                color: isDark ? '#ddd' : '#666',
                fontWeight: 600,
                '&:hover': {
                  borderColor: THEME_COLOR,
                  backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
                  color: THEME_COLOR,
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              fullWidth
              onClick={handleSave}
              disabled={saving || !!(formData.block_room && errors.block_room)}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{
                borderRadius: '12px',
                background: `linear-gradient(135deg, ${THEME_COLOR} 0%, #35c9a0 100%)`,
                color: '#04211a',
                fontWeight: 700,
                boxShadow: `0 4px 16px ${alpha(THEME_COLOR, 0.35)}`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${THEME_COLOR} 0%, #35c9a0 100%)`,
                  boxShadow: `0 6px 20px ${alpha(THEME_COLOR, 0.45)}`,
                  opacity: 0.95,
                },
                '&.Mui-disabled': {
                  background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
                  color: isDark ? '#777' : '#999',
                  boxShadow: 'none',
                },
              }}
            >
              {saving
                ? (editingRequest ? 'Updating…' : 'Adding…')
                : (editingRequest ? 'Update' : 'Add Maintenance Request')}
            </Button>
          </Box>
        </Drawer>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Delete Maintenance Request</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this maintenance request? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleDeleteConfirm} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default MaintenanceRequestSection;

